import { Checkbox, Divider, FormControlLabel } from "@mui/material";
import MarketplaceComponents from "../../MarketPlace";
import Textbox from "../../commonComponents/Textbox";
import Typography from "../../commonComponents/Typography";
import moment from "moment";
import {
  JoinerPayload,
  NewJoiner,
  NewJoinerTask,
  PersonaDefinition,
  SelectBoxOptions,
  onboardingOptions
} from "./OnboardingForm.type";
import React, { useEffect, useMemo, useState } from "react";

import {
  DatePickerWrapper,
  StyledActions,
  StyledDrawerContent,
  StyledForm,
} from "./OnboardingForm.style";
import DatePicker from "component/commonComponents/DatePicker";
import SelectBox from "component/commonComponents/selectBox";
import TaskList from "../TaskList";

export type OnboardingFormProps = {
  initData?: NewJoiner;
  open: boolean;
  onClose: () => void;
  onSubmit: (id: string, data: JoinerPayload) => void;
  onResetPw: (email: string, name: string) => void;
  onboardingOptions: onboardingOptions;
  isNewJoiner?: boolean;
  titleForm?: string;
  supportedCountries: any[];
  infoUserTenant: any;
  initJoinerData: any;
}

type JoinerKey = keyof JoinerPayload;
export interface Country {
  iso_alpha3: string;
  is_limited: boolean;
  name: string;
}

const formatDate = (date: moment.MomentInput = new Date()) => {
  return moment(date).toISOString();
};
const fifteenDayAfter = moment(new Date()).add(15, "days");

const defaultJoinerData: JoinerPayload = {
  first_name: "",
  last_name: "",
  personal_email: "",
  email: "",
  manager_email: "",
  start_date: formatDate(fifteenDayAfter),
  start_access_date: formatDate(fifteenDayAfter),
  job_title: "",
  type: "pre_joiner",
  status: "new",
  persona_definition: "",
  address: "",
  avatar: "",
  allow_order_device: true,
  tasks: [],
  created_by: "",
  updated_by: "",
  country: "",
  device_profile: "",
  ownership_model: "",
};

const OnboardingForm = (props: OnboardingFormProps) => {
  const {
    initData,
    open,
    onClose,
    onSubmit,
    onResetPw,
    onboardingOptions,
    isNewJoiner,
    titleForm,
    supportedCountries,
    infoUserTenant,
    initJoinerData,
  } = props;
  const [joinerData, setJoinerData] =
    useState<JoinerPayload>(defaultJoinerData);
  const { personaOptions, profileOptions, modelOptions } = onboardingOptions || {};
  const [errors, setErrors] = useState<{ [key in JoinerKey]?: boolean }>({});
  const [submitting, setSubmitting] = useState(false);

  const useSelectedOption = <T extends { [key: string]: any }>(options: T[], key: keyof T, value: string): T | undefined => {
    return useMemo(() => options?.find((option) => option[key] === value), [options, key, value]);
  };

  const selectedPersona = useSelectedOption(personaOptions, 'id', joinerData?.persona_definition);
  const selectedCountry = useMemo(() => {
    return (
      supportedCountries.find(
        (country: any) => country?.iso_alpha3 === joinerData.country
      ) || {}
    );
  }, [joinerData]);

  const populateData = initJoinerData.find(
    (p: any) => p.persona_definition === selectedPersona?.name
  );

  useEffect(() => {
    if (populateData && !initData) {
      setJoinerData({
        ...defaultJoinerData,
        persona_definition: selectedPersona?.id as string,
        first_name: joinerData?.first_name,
        last_name: joinerData?.last_name,
        job_title: populateData?.job_title,
        avatar: populateData?.avatar,
        email: joinerData?.email,
        personal_email: joinerData?.personal_email,
        start_date: joinerData?.start_date,
        manager_email: joinerData?.manager_email || infoUserTenant?.email,
        address: joinerData?.address,
        country: joinerData?.country,
        device_profile: joinerData?.device_profile,
        ownership_model: joinerData?.ownership_model,
      });
    }
  }, [populateData, initData]);

  useEffect(() => {
    if (!open) return;
    setJoinerData(initData || defaultJoinerData);
    setErrors({});
  }, [initData, open]);

  const handleClose = () => {
    onClose();
    setJoinerData(defaultJoinerData);
  };

  const handleChange = (key: JoinerKey, value: JoinerPayload[JoinerKey]) => {
    setJoinerData((prevState: any) => ({
      ...prevState,
      [key]: value,
    }));
    const newErrors = JSON.parse(JSON.stringify(errors));
    newErrors[key] = false;
    setErrors(newErrors);
  };

  const validateJoiner = () => {
    const errors: Record<string, boolean> = {};
    let isValid = true;
    const exceptKeys = [
      "id",
      "allow_order_device",
      "created_by",
      "updated_by",
      "latestOrder",
      "avatar",
      "device_profile",
      "ownership_model",
    ];
    Object.entries(joinerData).forEach(([key, value]) => {
      if (!exceptKeys.includes(key)) {
        errors[key] = !value;
        if (!value) isValid = false;
      }
    });
    if ((joinerData.ownership_model && !joinerData.device_profile) || (!joinerData.ownership_model && joinerData.device_profile)) {
      isValid = false;
    }
    setErrors(errors);
    return isValid;
  };
  const mapDefinitionToJoinerTask = () => {
    const newTask: NewJoinerTask[] =
      selectedPersona?.tasks.map((task: any) => ({
        task_definition_id: task.id,
        status: "pending",
      })) || [];
    return newTask;
  };
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const isValid = validateJoiner();
      if (isValid) {
        await onSubmit(initData?.id as string, {
          ...joinerData,
          created_by: infoUserTenant?.uid,
          updated_by: infoUserTenant?.uid,
          tasks: mapDefinitionToJoinerTask(),
        });
        handleClose();
      }
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setSubmitting(false);
    }
  };
  const handleResetPassword = async () => {
    try {
      const isValid = validateJoiner();
      if (isValid) {
        onResetPw(
          joinerData?.email,
          `${joinerData?.first_name} ${joinerData?.last_name}`
        );
        handleClose();
      }
    } catch (error) {
      console.error("Failed:", error);
    }
  };

  return (
    <MarketplaceComponents.Drawer open={open} onClose={handleClose}>
      <StyledDrawerContent>
        <StyledForm>
          <Typography kind="h6">{titleForm}</Typography>

          <Textbox
            name="first_name"
            label="First Name"
            placeholder="First Name"
            boldLabel
            labelSize="small"
            error={!!errors.first_name}
            value={joinerData.first_name || ""}
            onChange={(e: any) => handleChange("first_name", e.target.value)}
            fullWidth
            required
            disabled={!!isNewJoiner}
          />
          <Textbox
            name="last_name"
            label="Last Name"
            placeholder="Last Name"
            boldLabel
            labelSize="small"
            error={!!errors.last_name}
            value={joinerData.last_name || ""}
            onChange={(e: any) => handleChange("last_name", e.target.value)}
            fullWidth
            required
            disabled={!!isNewJoiner}
          />
          <Textbox
            name="personal_email"
            label="Personal Email"
            placeholder="Personal Email"
            boldLabel
            labelSize="small"
            error={!!errors.personal_email}
            value={joinerData.personal_email || ""}
            onChange={(e: any) =>
              handleChange("personal_email", e.target.value)
            }
            fullWidth
            required
            disabled={!!isNewJoiner}
          />
          <Textbox
            name="email"
            label="Email"
            placeholder="Email"
            boldLabel
            labelSize="small"
            error={!!errors.email}
            value={joinerData.email || ""}
            onChange={(e: any) => handleChange("email", e.target.value)}
            fullWidth
            required
            disabled={!!isNewJoiner}
          />
          <Textbox
            name="manager_email"
            label="Manager Email"
            placeholder="Manager Email"
            boldLabel
            labelSize="small"
            error={!!errors.manager_email}
            value={joinerData.manager_email || ""}
            onChange={(e: any) => handleChange("manager_email", e.target.value)}
            fullWidth
            required
            disabled={!!isNewJoiner}
          />
          <DatePickerWrapper>
            <DatePicker
              label="Start Date"
              boldLabel
              value={moment(joinerData.start_date)}
              onChange={(date: any) =>
                handleChange("start_date", formatDate(date))
              }
              required
              error={!!errors.start_date}
              useInitValue={false}
              disablePast
              disabled={!!isNewJoiner}
            />
          </DatePickerWrapper>
          <DatePickerWrapper>
            <DatePicker
              label="Start Access Date"
              boldLabel
              value={moment(joinerData.start_access_date)}
              onChange={(date) =>
                handleChange("start_access_date", formatDate(date))
              }
              required
              error={!!errors.start_access_date}
              useInitValue={false}
              disablePast
              disabled={!!isNewJoiner}
            />
          </DatePickerWrapper>
          <Textbox
            name="job_title"
            label="Job Title"
            placeholder="Job Title"
            boldLabel
            labelSize="small"
            error={!!errors.job_title}
            value={joinerData.job_title || ""}
            onChange={(e: any) => handleChange("job_title", e.target.value)}
            fullWidth
            required
            disabled={!!isNewJoiner}
          />
          <SelectBox
            name="persona_definition"
            label="Persona Definition"
            placeholder="Persona Definition"
            boldLabel
            handleChange={(e: unknown, value: PersonaDefinition) =>
              handleChange("persona_definition", value.id)
            }
            options={personaOptions}
            value={selectedPersona || {}}
            error={!!errors.persona_definition}
            valueKey="name"
            disableClearable
            required
            disabled={!!isNewJoiner}
          />
          <SelectBox
            name="country"
            label="Country"
            placeholder="Country"
            boldLabel
            handleChange={(_: any, value: Country) =>
              handleChange("country", value.iso_alpha3)
            }
            options={supportedCountries}
            value={selectedCountry}
            error={!!errors.country}
            valueKey="iso_alpha3"
            disableClearable
            required
            disabled={!!isNewJoiner}
          />
          <Textbox
            name="address"
            label="Address"
            placeholder="Address"
            boldLabel
            labelSize="small"
            error={!!errors.address}
            value={joinerData.address || ""}
            onChange={(e: any) => handleChange("address", e.target.value)}
            fullWidth
            required
          />
          <Textbox
            name="avatar"
            label="Avatar"
            placeholder="Avatar"
            boldLabel
            labelSize="small"
            value={joinerData.avatar || ""}
            onChange={(e: any) => handleChange("avatar", e.target.value)}
            error={!!errors.avatar}
            fullWidth
          />
          <SelectBox
            name="device_profile"
            label="Device Profile"
            error={!!joinerData.ownership_model && !joinerData.device_profile}
            placeholder="Device Profile"
            boldLabel
            value={{ id: joinerData.device_profile ,name: joinerData.device_profile }}
            handleChange={(e: unknown, value: SelectBoxOptions) => handleChange("device_profile", value?.name || '')}
            options={profileOptions}
            valueKey="device_profile"
            fullWidth
          />
          <SelectBox
            name="ownership_model"
            label="Ownership Model"
            error={!joinerData.ownership_model && !!joinerData.device_profile}
            placeholder="Ownership Model"
            boldLabel
            value={{ id: joinerData.ownership_model, name: joinerData.ownership_model }}
            handleChange={(e: unknown, value: SelectBoxOptions) => handleChange("ownership_model", value?.name || '')}
            options={modelOptions}
            valueKey="ownership_model"
            fullWidth
          />
          {!isNewJoiner && (
            <FormControlLabel
              label="Allow Order Device"
              control={
                <Checkbox
                  checked={!!joinerData.allow_order_device}
                  onChange={(e) =>
                    handleChange("allow_order_device", e.target.checked)
                  }
                />
              }
            />
          )}

          <Divider />

          <TaskList
            tasks={selectedPersona?.tasks || []}
            title="Training Tasks"
          />
        </StyledForm>

        <Divider />

        <StyledActions>
          <MarketplaceComponents.Button
            fullWidth
            loadingStart={submitting}
            onClick={handleSubmit}
            id="btn-onboarding_form-submit"
          >
            Submit
          </MarketplaceComponents.Button>
          {joinerData?.tasks?.length > 0 && (
            <MarketplaceComponents.Button
              fullWidth
              onClick={handleResetPassword}
            >
              Reset
            </MarketplaceComponents.Button>
          )}
        </StyledActions>
      </StyledDrawerContent>
    </MarketplaceComponents.Drawer>
  );
};

export default OnboardingForm;